import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { LocationIndexQueryKey, LocationShowQueryKey } from './locations.query'
import type {
  CreatedLocation,
  CreateLocationInput,
  DeletedLocation,
  DeliveryFeePreview,
  DeliveryFeePreviewInput,
  DeliveryPostalCodeFeeImportInput,
  DeliveryPostalCodeFeeImportResult,
  Location,
  LocationDeliveryAvailabilitySettingsInput,
  LocationDeliveryPricingSettingsInput,
  LocationFulfillmentPreview,
  LocationFulfillmentPreviewInput,
  LocationHoursSettingsInput,
  LocationId,
  LocationSettingsInput,
  UpdatedLocation,
  UpdateLocationInput,
} from './locations.types'

interface OrganizationScope {
  organizationId: OrganizationId
}

interface LocationScope extends OrganizationScope {
  locationId: LocationId
}

export async function getLocations({
  queryKey: [{ organizationId, filters = {} }],
}: QueryFunctionContext<LocationIndexQueryKey>) {
  return (
    (await axios.get<Location[]>(
      '/locations',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getLocation({
  queryKey: [{ organizationId, locationId, filters = {} }],
}: QueryFunctionContext<LocationShowQueryKey>) {
  return (
    (await axios.get<Location>(
      `/locations/${locationId}`,
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || {}
  )
}

export async function createLocation({
  organizationId,
  ...values
}: CreateLocationInput & OrganizationScope) {
  return axios.post<CreatedLocation>(
    '/locations',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateLocation({
  organizationId,
  locationId,
  ...values
}: UpdateLocationInput & LocationScope) {
  return axios.put<UpdatedLocation>(
    `/locations/${locationId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateLocationSettings({
  organizationId,
  locationId,
  ...values
}: LocationSettingsInput & LocationScope) {
  return axios.put<Location>(
    `/locations/${locationId}/settings`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateLocationHoursSettings({
  organizationId,
  locationId,
  ...values
}: LocationHoursSettingsInput & LocationScope) {
  return axios.put<Location>(
    `/locations/${locationId}/hours-settings`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateLocationDeliveryAvailabilitySettings({
  organizationId,
  locationId,
  ...values
}: LocationDeliveryAvailabilitySettingsInput & LocationScope) {
  return axios.put<Location>(
    `/locations/${locationId}/delivery-availability-settings`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateLocationDeliveryPricingSettings({
  organizationId,
  locationId,
  ...values
}: LocationDeliveryPricingSettingsInput & LocationScope) {
  return axios.put<Location>(
    `/locations/${locationId}/delivery-pricing-settings`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function previewLocationFulfillmentDate({
  organizationId,
  locationId,
  ...values
}: LocationFulfillmentPreviewInput & LocationScope) {
  return axios.post<LocationFulfillmentPreview>(
    `/locations/${locationId}/fulfillment-preview`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function previewLocationDeliveryFee({
  organizationId,
  locationId,
  ...values
}: DeliveryFeePreviewInput & LocationScope) {
  return axios.post<DeliveryFeePreview>(
    `/locations/${locationId}/delivery-fee-preview`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function previewLocationDeliveryPostalCodeFeeImport({
  organizationId,
  locationId,
  file,
}: DeliveryPostalCodeFeeImportInput & LocationScope) {
  const formData = new FormData()
  formData.append('file', file)

  return axios.post<DeliveryPostalCodeFeeImportResult>(
    `/locations/${locationId}/delivery-postal-code-fees/import/preview`,
    formData,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function importLocationDeliveryPostalCodeFees({
  organizationId,
  locationId,
  file,
}: DeliveryPostalCodeFeeImportInput & LocationScope) {
  const formData = new FormData()
  formData.append('file', file)

  return axios.post<DeliveryPostalCodeFeeImportResult>(
    `/locations/${locationId}/delivery-postal-code-fees/import`,
    formData,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function softDeleteLocation({ organizationId, locationId }: LocationScope) {
  return axios.delete<DeletedLocation>(
    `/locations/${locationId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
